/**
 * Fall-Filter — gemeinsame Filter-Komponente für Fallübersichten (Forderungen, Stellungnahmen).
 *
 * Die Zustandsform ist bewusst generisch gehalten: Einzel-Selects werden als Enum-Strings
 * gespeichert, Multi-Selects als String-Arrays, Datumswerte als ISO (yyyy-mm-dd).
 * Active Filter werden in der URL-Query serialisiert, damit Deep-Links und Browser-Back
 * deterministisch funktionieren (Issue #264).
 */

// ─── Typen ───────────────────────────────────────────────────────────────
export type TriState = 'ja' | 'nein' | 'offen';
export type YesNo = 'ja' | 'nein';
export type VerjaehrungTage = 30 | 60 | 90 | 180;
export type Plausibilitaet = 'gruen' | 'gelb' | 'rot';

export interface FallFilterState {
  businessUnit: string[];
  status: string[];
  versicherung: string[];
  abtretung: TriState | null;
  anwalt: YesNo | null;
  schadennummer: YesNo | null;
  verjaehrungTage: VerjaehrungTage | null;
  plausibilitaet: Plausibilitaet[];
  betragMin: number | null;
  betragMax: number | null;
  rechnungVon: string | null;
  rechnungBis: string | null;
  mietbeginnVon: string | null;
  mietbeginnBis: string | null;
}

export const EMPTY_FALL_FILTER: FallFilterState = {
  businessUnit: [],
  status: [],
  versicherung: [],
  abtretung: null,
  anwalt: null,
  schadennummer: null,
  verjaehrungTage: null,
  plausibilitaet: [],
  betragMin: null,
  betragMax: null,
  rechnungVon: null,
  rechnungBis: null,
  mietbeginnVon: null,
  mietbeginnBis: null,
};

const TRI_VALUES: readonly TriState[] = ['ja', 'nein', 'offen'];
const YES_NO_VALUES: readonly YesNo[] = ['ja', 'nein'];
const VERJAEHRUNG_VALUES: readonly VerjaehrungTage[] = [30, 60, 90, 180];
const PLAUSIBILITAET_VALUES: readonly Plausibilitaet[] = ['gruen', 'gelb', 'rot'];

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// ─── Helpers ─────────────────────────────────────────────────────────────
export function isFallFilterEmpty(f: FallFilterState): boolean {
  return (
    f.businessUnit.length === 0 &&
    f.status.length === 0 &&
    f.versicherung.length === 0 &&
    f.abtretung === null &&
    f.anwalt === null &&
    f.schadennummer === null &&
    f.verjaehrungTage === null &&
    f.plausibilitaet.length === 0 &&
    f.betragMin === null &&
    f.betragMax === null &&
    f.rechnungVon === null &&
    f.rechnungBis === null &&
    f.mietbeginnVon === null &&
    f.mietbeginnBis === null
  );
}

function parseIsoDate(value: string | null): string | null {
  if (!value || !ISO_DATE_RE.test(value)) return null;
  return value;
}

function parseNumber(value: string | null): number | null {
  if (value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function splitList(value: string | null): string[] {
  if (!value) return [];
  return value.split(',').map((s) => s.trim()).filter(Boolean);
}

function filterToAllowed<T extends string>(values: string[], allowed: readonly T[]): T[] {
  const set = new Set<string>(allowed);
  return values.filter((v): v is T => set.has(v));
}

// ─── Parse ──────────────────────────────────────────────────────────────
export function parseFallFilterFromSearch(search: string): FallFilterState {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);

  const abtRaw = q.get('abtretung');
  const anwaltRaw = q.get('anwalt');
  const schadenRaw = q.get('schadennummer');
  const verjRaw = q.get('verjaehrung');
  const verjNum = verjRaw === null ? null : Number(verjRaw);

  return {
    businessUnit: splitList(q.get('bu')),
    status: splitList(q.get('status')),
    versicherung: splitList(q.get('versicherung')),
    abtretung: abtRaw && (TRI_VALUES as readonly string[]).includes(abtRaw) ? (abtRaw as TriState) : null,
    anwalt: anwaltRaw && (YES_NO_VALUES as readonly string[]).includes(anwaltRaw) ? (anwaltRaw as YesNo) : null,
    schadennummer: schadenRaw && (YES_NO_VALUES as readonly string[]).includes(schadenRaw) ? (schadenRaw as YesNo) : null,
    verjaehrungTage: verjNum !== null && (VERJAEHRUNG_VALUES as readonly number[]).includes(verjNum) ? (verjNum as VerjaehrungTage) : null,
    plausibilitaet: filterToAllowed(splitList(q.get('plausibilitaet')), PLAUSIBILITAET_VALUES),
    betragMin: parseNumber(q.get('betrag_min')),
    betragMax: parseNumber(q.get('betrag_max')),
    rechnungVon: parseIsoDate(q.get('rechnung_von')),
    rechnungBis: parseIsoDate(q.get('rechnung_bis')),
    mietbeginnVon: parseIsoDate(q.get('mietbeginn_von')),
    mietbeginnBis: parseIsoDate(q.get('mietbeginn_bis')),
  };
}

// ─── Serialize ──────────────────────────────────────────────────────────
export function serializeFallFilterToSearch(f: FallFilterState): string {
  const q = new URLSearchParams();
  if (f.businessUnit.length) q.set('bu', f.businessUnit.join(','));
  if (f.status.length) q.set('status', f.status.join(','));
  if (f.versicherung.length) q.set('versicherung', f.versicherung.join(','));
  if (f.abtretung) q.set('abtretung', f.abtretung);
  if (f.anwalt) q.set('anwalt', f.anwalt);
  if (f.schadennummer) q.set('schadennummer', f.schadennummer);
  if (f.verjaehrungTage !== null) q.set('verjaehrung', String(f.verjaehrungTage));
  if (f.plausibilitaet.length) q.set('plausibilitaet', f.plausibilitaet.join(','));
  if (f.betragMin !== null && Number.isFinite(f.betragMin)) q.set('betrag_min', String(f.betragMin));
  if (f.betragMax !== null && Number.isFinite(f.betragMax)) q.set('betrag_max', String(f.betragMax));
  if (f.rechnungVon) q.set('rechnung_von', f.rechnungVon);
  if (f.rechnungBis) q.set('rechnung_bis', f.rechnungBis);
  if (f.mietbeginnVon) q.set('mietbeginn_von', f.mietbeginnVon);
  if (f.mietbeginnBis) q.set('mietbeginn_bis', f.mietbeginnBis);
  return q.toString();
}

// ─── Matching (clientseitig) ────────────────────────────────────────────
/**
 * Shape eines Falls wie ihn die Forderungsübersicht verwendet. Bewusst offen gehalten,
 * damit andere Fallübersichten (z. B. Stellungnahmen in #243) die gleiche Struktur
 * liefern können; fehlende Felder zählen als "nicht gesetzt".
 */
export interface FallFilterInput {
  versicherung_gegner?: string | null;
  schaden_nummer?: string | null;
  mahnstufe?: string | null;
  abtretung_status?: string | null;
  verjährungsfrist?: string | null;
  gesamtbetrag?: number | null;
  rechnung_datum?: string | null;
  mietbeginn?: string | null;
  status?: string | null;
  business_unit?: string | null;
  plausibilitaet?: Plausibilitaet | null;
}

function abtretungMatches(row: FallFilterInput, value: TriState): boolean {
  const raw = row.abtretung_status ?? null;
  if (value === 'ja') return raw === 'unterzeichnet' || raw === 'versendet';
  if (value === 'offen') return raw === 'ausstehend';
  return raw === null;
}

function yesNoPresenceMatches(value: string | null | undefined, flag: YesNo): boolean {
  const present = typeof value === 'string' && value.trim().length > 0;
  return flag === 'ja' ? present : !present;
}

function dateInRange(iso: string | null | undefined, von: string | null, bis: string | null): boolean {
  if (!von && !bis) return true;
  if (!iso) return false;
  if (von && iso < von) return false;
  if (bis && iso > bis) return false;
  return true;
}

export function matchesFallFilter(row: FallFilterInput, f: FallFilterState): boolean {
  if (f.businessUnit.length && !f.businessUnit.includes(row.business_unit ?? '')) return false;
  if (f.status.length && !f.status.includes(row.status ?? '')) return false;
  if (f.versicherung.length && !f.versicherung.includes(row.versicherung_gegner ?? '')) return false;
  if (f.abtretung && !abtretungMatches(row, f.abtretung)) return false;
  if (f.anwalt) {
    const istAnwalt = (row.mahnstufe ?? '') === 'anwalt';
    if (f.anwalt === 'ja' && !istAnwalt) return false;
    if (f.anwalt === 'nein' && istAnwalt) return false;
  }
  if (f.schadennummer && !yesNoPresenceMatches(row.schaden_nummer, f.schadennummer)) return false;
  if (f.verjaehrungTage !== null) {
    const frist = row.verjährungsfrist ?? null;
    if (!frist) return false;
    const diffDays = Math.ceil((new Date(frist).getTime() - Date.now()) / 86_400_000);
    if (!(diffDays >= 0 && diffDays <= f.verjaehrungTage)) return false;
  }
  if (f.plausibilitaet.length) {
    const p = row.plausibilitaet ?? null;
    if (!p || !f.plausibilitaet.includes(p)) return false;
  }
  const betrag = row.gesamtbetrag ?? null;
  if (f.betragMin !== null) {
    if (betrag === null || betrag < f.betragMin) return false;
  }
  if (f.betragMax !== null) {
    if (betrag === null || betrag > f.betragMax) return false;
  }
  if (!dateInRange(row.rechnung_datum ?? null, f.rechnungVon, f.rechnungBis)) return false;
  if (!dateInRange(row.mietbeginn ?? null, f.mietbeginnVon, f.mietbeginnBis)) return false;
  return true;
}

// ─── Aktive Filter zählen (für UI-Badge) ────────────────────────────────
export function countActiveFallFilters(f: FallFilterState): number {
  let n = 0;
  if (f.businessUnit.length) n++;
  if (f.status.length) n++;
  if (f.versicherung.length) n++;
  if (f.abtretung) n++;
  if (f.anwalt) n++;
  if (f.schadennummer) n++;
  if (f.verjaehrungTage !== null) n++;
  if (f.plausibilitaet.length) n++;
  if (f.betragMin !== null || f.betragMax !== null) n++;
  if (f.rechnungVon || f.rechnungBis) n++;
  if (f.mietbeginnVon || f.mietbeginnBis) n++;
  return n;
}
